import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { userApi } from '../../api/userApi';
import ProductCard from '../components/ProductCard';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FiHeart } from 'react-icons/fi';
import FilterSidebar from '../components/FilterSidebar';

const NewArrivals = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    categories: ['All'],
    sizes: ['All'],
    colors: ['All'],
    brands: ['All'],
    materials: ['All'],
    occasions: ['All'],
    availability: ['All'],
    ratings: ['All'],
    discounts: ['All'],
    priceRange: [0, 100000]
  });
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    sizes: [],
    colors: [],
    brands: [],
    materials: ['Silk', 'Cotton', 'Chiffon', 'Georgette', 'Velvet'],
    occasions: ['Wedding', 'Party', 'Casual', 'Festival', 'Office'],
    availability: ['In Stock', 'Out of Stock'],
    ratings: ['4★ & above', '3★ & above', '2★ & above'],
    discounts: ['10% & above', '20% & above', '30% & above', '50% & above'],
    priceRange: {
      min: 0,
      max: 100000,
      step: 1000
    }
  });

  const clearAllFilters = () => {
    setSelectedFilters({
      categories: ['All'],
      sizes: ['All'],
      colors: ['All'],
      brands: ['All'],
      materials: ['All'],
      occasions: ['All'],
      availability: ['All'],
      ratings: ['All'],
      discounts: ['All'],
      priceRange: [0, 100000]
    });
  };

  // Add function to handle wishlist toggle
  const handleWishlistToggle = async (productId) => {
    try {
      await userApi.addToWishlist(productId);
      
      setWishlistItems(prev => {
        const newSet = new Set(prev);
        newSet.add(productId);
        return newSet;
      });
      
      toast.success('Added to wishlist');
      window.dispatchEvent(new Event('wishlistUpdated'));
    } catch (error) {
      if (error.message.includes('login')) {
        navigate('/login');
        return;
      }
      toast.error(error.message || 'Failed to update wishlist');
    }
  };

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const wishlistData = await userApi.getWishlist();
        const wishlistIds = new Set(wishlistData.map(item => item.product?.id));
        setWishlistItems(wishlistIds);
      } catch (error) {
        console.error('Error fetching wishlist:', error);
      }
    };

    fetchWishlist();
  }, []);

  useEffect(() => {
    const fetchNewArrivals = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getNewArrivals();
        
        const transformedProducts = data.map(product => ({
          id: product.id,
          name: product.name,
          price: product.price,
          salePrice: product.sale_price,
          finalPrice: product.final_price, // Add final price
          category: product.category.name,
          brand: product.brand?.name || '',
          discount: product.discount,
          images: product.variants.reduce((acc, variant) => {
            const variantImages = variant.images.map(img => ({
              url: img.images,
              alt: `${product.name} - ${variant.color}`
            }));
            return [...acc, ...variantImages];
          }, []),
          variants: product.variants,
          isNewIn: true,
          isOffer: product.active_offers?.length > 0,
          activeOffers: product.active_offers, // Add active offers
          stock: product.variants.reduce((total, variant) => total + variant.stock, 0),
        }));

        setProducts(transformedProducts);
        
        // Update filter options based on products
        const categories = [...new Set(transformedProducts.map(p => p.category))];
        const brands = [...new Set(transformedProducts.map(p => p.brand))];
        const sizes = [...new Set(transformedProducts.flatMap(p => 
          p.variants?.map(v => v.size) || []
        ))];
        const colors = [...new Set(transformedProducts.flatMap(p => 
          p.variants?.map(v => v.color) || []
        ))];

        setFilterOptions(prev => ({
          ...prev,
          categories,
          brands,
          sizes,
          colors
        }));

      } catch (error) {
        console.error('Failed to fetch new arrivals:', error);
        toast.error('Failed to load new arrivals');
      } finally {
        setIsLoading(false);
      }
    };

    fetchNewArrivals();
  }, []);

  return (
    <>
      <Helmet>
        <title>New Arrivals | Panchali Boutique</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-light mb-4">New Arrivals</h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-indigo-500 to-purple-500 mx-auto"></div>
        </motion.div>

        {isLoading ? (
          // Loading State
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2C3E50] border-t-transparent"></div>
          </div>
        ) : products.length === 0 ? (
          // Empty State
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No new arrivals at the moment.</p>
          </div>
        ) : (
          // Products Grid
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="h-full"
              >
                <ProductCard 
                  product={{
                    ...product,
                    finalPrice: product.finalPrice,
                    activeOffers: product.activeOffers,
                    isNewIn: true
                  }}
                  className="h-full"
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Sidebar */}
      <FilterSidebar
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        filterOptions={filterOptions}
        clearAllFilters={clearAllFilters}
      />
    </>
  );
};

export default NewArrivals;



